import React, { useState, useEffect } from 'react';
import { BookmarkPlus, CheckCircle2, AlertTriangle } from 'lucide-react';
import Button from './Button';
import TextInput from './TextInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { RubricTemplateCriterion } from '../src/types';
import { useAssessmentStore } from '../src/store/assessmentStore';

const MAX_NAME_LENGTH = 80;

interface SaveRubricTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultName: string;
  totalMarks: number;
  criteria: RubricTemplateCriterion[];
  sourceAssessmentId?: string;
}

const SaveRubricTemplateModal: React.FC<SaveRubricTemplateModalProps> = ({
  isOpen,
  onClose,
  defaultName,
  totalMarks,
  criteria,
  sourceAssessmentId,
}) => {
  const rubricTemplates = useAssessmentStore(state => state.rubricTemplates);
  const saveRubricTemplate = useAssessmentStore(state => state.saveRubricTemplate);

  const [name, setName] = useState(defaultName);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [conflictId, setConflictId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(defaultName.slice(0, MAX_NAME_LENGTH));
      setError(null);
      setIsSaving(false);
      setConflictId(null);
      setSaved(false);
    }
  }, [isOpen, defaultName]);

  const doSave = async (overwriteId?: string) => {
    setIsSaving(true);
    setError(null);
    try {
      await saveRubricTemplate({
        name: name.trim(),
        totalMarks,
        criteria,
        sourceAssessmentId,
        overwriteId,
      });
      setSaved(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template.');
      setConflictId(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter a template name.');
      return;
    }
    const existing = rubricTemplates.find(
      t => t.name.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (existing) {
      setConflictId(existing.id);
      return;
    }
    doSave();
  };

  return (
    <Dialog open={isOpen} onOpenChange={isSaving ? () => {} : onClose}>
      <DialogContent onClose={isSaving ? undefined : onClose} showCloseButton={!isSaving}>
        {saved ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="size-6 text-green-600" />
            </div>
            <p className="text-lg font-semibold text-foreground">Template saved</p>
            <p className="text-sm text-muted-foreground">
              &ldquo;{name.trim()}&rdquo; is now available when creating a new assessment.
            </p>
          </div>
        ) : conflictId ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-full bg-amber-100">
                  <AlertTriangle className="size-5 text-amber-600" />
                </div>
                <DialogTitle>Template already exists</DialogTitle>
              </div>
              <DialogDescription className="pt-2">
                A template named &ldquo;{name.trim()}&rdquo; already exists. Overwrite it with
                this rubric, or go back and choose a different name.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setConflictId(null)} disabled={isSaving}>
                Back
              </Button>
              <Button
                type="button"
                onClick={() => doSave(conflictId)}
                isLoading={isSaving}
                loadingText="Overwriting..."
              >
                Overwrite
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
                  <BookmarkPlus className="size-5 text-primary" />
                </div>
                <DialogTitle>Save Rubric as Template</DialogTitle>
              </div>
              <DialogDescription className="pt-2">
                {criteria.length} criteri{criteria.length === 1 ? 'on' : 'a'} &middot; {totalMarks} marks.
                Saved templates can be applied when setting up a new assessment.
              </DialogDescription>
            </DialogHeader>
            <div className="pt-2">
              <TextInput
                label="Template Name"
                id="rubric-template-name"
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setName(e.target.value.slice(0, MAX_NAME_LENGTH));
                  setError(null);
                }}
                placeholder="e.g., Year 12 Essay Rubric (20 marks)"
                required
                disabled={isSaving}
                error={error || undefined}
              />
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} isLoading={isSaving} loadingText="Saving...">
                Save Template
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SaveRubricTemplateModal;
